import { z } from 'zod';
import type { BlockDefinition } from '../types';

export const calculatorSchema = z.object({
  eyebrow: z.string().max(120).optional(),
  h2: z.string().min(1).max(200),
  subtitle: z.string().max(400).optional(),
  inputs: z
    .array(
      z.object({
        name: z.string().min(1).max(40),
        label: z.string().min(1).max(120),
        default_value: z.number().default(0).optional(),
        min: z.number().optional(),
        max: z.number().optional(),
        step: z.number().default(1).optional(),
        suffix: z.string().max(20).optional(),
      }),
    )
    .min(1)
    .max(6),
  formula: z.string().min(1).max(500),
  result_label: z.string().min(1).max(120),
  result_prefix: z.string().max(20).optional(),
  result_suffix: z.string().max(20).optional(),
  cta_label: z.string().max(80).optional(),
  cta_href: z.string().max(500).optional(),
});

export type CalculatorData = z.infer<typeof calculatorSchema>;

export function CalculatorRenderer({
  eyebrow, h2, subtitle, inputs, formula, result_label, result_prefix, result_suffix, cta_label, cta_href,
}: CalculatorData) {
  return (
    <section className="v5-section v5-calculator-section">
      <div className="container">
        <div className="v5-section-head v5-section-head-left">
          {eyebrow ? <div className="v5-eyebrow">{eyebrow}</div> : null}
          <h2 className="v5-h2">{h2}</h2>
          {subtitle ? <p className="v5-lead">{subtitle}</p> : null}
        </div>
        <div
          className="v5-calculator"
          data-calculator
          data-formula={formula}
          data-result-prefix={result_prefix ?? ''}
          data-result-suffix={result_suffix ?? ''}
        >
          <div className="v5-calculator-inputs">
            {inputs.map((inp, i) => (
              <label key={i} className="v5-calculator-input">
                <span>{inp.label}</span>
                <input
                  type="number"
                  data-var={inp.name}
                  defaultValue={inp.default_value ?? 0}
                  min={inp.min}
                  max={inp.max}
                  step={inp.step ?? 1}
                />
                {inp.suffix ? <em>{inp.suffix}</em> : null}
              </label>
            ))}
          </div>
          <div className="v5-calculator-result">
            <span className="v5-calculator-result-label">{result_label}</span>
            <strong className="v5-calculator-result-value">
              {result_prefix}<span data-result>—</span>{result_suffix}
            </strong>
            {cta_label && cta_href ? (
              <a className="v5-btn-primary" href={cta_href}>{cta_label}</a>
            ) : null}
          </div>
        </div>
      </div>
    </section>
  );
}

export const calculatorBlock: BlockDefinition<CalculatorData> = {
  meta: {
    type: 'calculator',
    label: 'Calculadora interactiva',
    description: 'Genera una calculadora (precio, ROI, valoración) con inputs personalizados y fórmula JS. Auto-actualización en cliente.',
    category: 'cta',
    icon: 'calculator',
  },
  schema: calculatorSchema,
  defaults: {
    eyebrow: 'CALCULADORA',
    h2: 'Estima tu valoración',
    subtitle: 'Ajusta tus números y obtén una estimación en segundos.',
    inputs: [
      { name: 'ebitda', label: 'EBITDA anual (€)', default_value: 500000, step: 10000, suffix: '€' },
      { name: 'multiplo', label: 'Múltiplo del sector', default_value: 6, step: 0.1, suffix: 'x' },
    ],
    formula: 'ebitda * multiplo',
    result_label: 'Valoración estimada',
    result_prefix: '€ ',
    cta_label: 'Solicitar valoración profesional',
    cta_href: '/contacto',
  },
  fields: [
    { name: 'eyebrow', label: 'Eyebrow', type: 'text', max: 120 },
    { name: 'h2', label: 'Título', type: 'text', required: true, max: 200 },
    { name: 'subtitle', label: 'Subtítulo', type: 'textarea', max: 400 },
    {
      name: 'inputs',
      label: 'Variables de entrada',
      type: 'array',
      max: 6,
      itemFields: [
        { name: 'name', label: 'Nombre (variable JS)', type: 'text', required: true, max: 40, helperText: 'Solo letras y números. Ej: ebitda, ingresos.' },
        { name: 'label', label: 'Etiqueta visible', type: 'text', required: true, max: 120 },
        { name: 'default_value', label: 'Valor por defecto', type: 'number' },
        { name: 'min', label: 'Min', type: 'number' },
        { name: 'max', label: 'Max', type: 'number' },
        { name: 'step', label: 'Paso', type: 'number' },
        { name: 'suffix', label: 'Sufijo', type: 'text', max: 20, placeholder: '€, %, x' },
      ],
    },
    { name: 'formula', label: 'Fórmula (expresión JS)', type: 'text', required: true, max: 500, helperText: 'Usa los nombres de las variables. Ej: ebitda * multiplo' },
    { name: 'result_label', label: 'Etiqueta del resultado', type: 'text', required: true, max: 120 },
    { name: 'result_prefix', label: 'Prefijo resultado', type: 'text', max: 20 },
    { name: 'result_suffix', label: 'Sufijo resultado', type: 'text', max: 20 },
    { name: 'cta_label', label: 'Texto CTA', type: 'text', max: 80 },
    { name: 'cta_href', label: 'URL CTA', type: 'url' },
  ],
  Renderer: CalculatorRenderer,
};
